import { readStorage, writeStorage } from '@/lib/storage'

export interface Attribution {
  source: string | null
  medium: string | null
  campaign: string | null
  content: string | null
  term: string | null
  gclid: string | null
  fbclid: string | null
  referrer: string | null
  landingPath: string | null
  firstSeenAt: string | null
}

interface CaptureInput {
  search: string
  referrer: string
  pathname: string
  now?: Date
}

const FIRST_TOUCH_KEY = 'ab.attribution'
const LAST_TOUCH_KEY = 'ab.attribution.last'
const PAID_SESSION_KEY = 'ab.paid'
const MAX_LENGTH = 500
const PAID_PARAMETERS = new Set(['fbclid', 'gclid'])

function clean(value: string | null | undefined) {
  return value?.trim().slice(0, MAX_LENGTH) || null
}

function withoutWww(host: string) {
  return host.replace(/^www\./, '')
}

function isExternalReferrer(referrer: string) {
  if (!referrer) return false
  try {
    return withoutWww(new URL(referrer).host) !== withoutWww(location.host)
  } catch {
    return false
  }
}

export function getAttribution(): Attribution | null {
  return readStorage<Attribution | null>(FIRST_TOUCH_KEY, null)
}

export function getLastAttribution(): Attribution | null {
  return readStorage<Attribution | null>(LAST_TOUCH_KEY, null)
}

export function isPaidSession(): boolean {
  return readStorage<boolean>(PAID_SESSION_KEY, false, 'session')
}

function isPaidVisit(search: string) {
  for (const key of new URLSearchParams(search).keys()) {
    const name = key.toLowerCase()
    if (name.startsWith('utm_') || PAID_PARAMETERS.has(name)) return true
  }
  return false
}

export function arrivedFromAd(): boolean {
  return isPaidVisit(window.location.search) || isPaidSession()
}

export function captureAttribution({ search, referrer, pathname, now = new Date() }: CaptureInput) {
  const params = new URLSearchParams(search)
  const tagged =
    [...params.keys()].some((key) => key.startsWith('utm_')) ||
    params.has('gclid') ||
    params.has('fbclid')
  if (tagged) writeStorage(PAID_SESSION_KEY, true, 'session')
  const external = isExternalReferrer(referrer)
  if (!tagged && (!external || clean(referrer) === getLastAttribution()?.referrer)) return

  const touch: Attribution = {
    source: clean(params.get('utm_source')),
    medium: clean(params.get('utm_medium')),
    campaign: clean(params.get('utm_campaign')),
    content: clean(params.get('utm_content')),
    term: clean(params.get('utm_term')),
    gclid: clean(params.get('gclid')),
    fbclid: clean(params.get('fbclid')),
    referrer: external ? clean(referrer) : null,
    landingPath: clean(pathname),
    firstSeenAt: now.toISOString(),
  }
  writeStorage(LAST_TOUCH_KEY, touch)
  if (getAttribution() === null) writeStorage(FIRST_TOUCH_KEY, touch)
}
